---
title: "Application questions and forms"
description: "Build a job application form in Chosen: built-in fields, custom question types, required vs optional, file size limits, and why short forms convert better."
category: "careers"
keywords:
  - "job application form"
  - "application questions ATS"
  - "screening questions"
  - "application form length"
schema: "HowTo"
nav:
  section: "guides"
  order: 30
  parent: "/guides/careers"
---

Every job has an application form. Name, email, and resume are built in and always there. Beyond that, you add your own questions per job — text, multiple choice, a URL, or a short video. Keep the list short: every extra required question costs you applicants.

## What's already on the form

Three fields come with every job and can't be removed:

- **Name**
- **Email**
- **Resume** — a PDF, DOC, or DOCX up to 10 MB. Chosen parses it into structured fields automatically; see [resume parsing](/guides/candidates/resume-parsing).

For a lot of roles, those three are the whole form. You don't have to add anything.

## Add custom questions

Job questions are edited per job, in the **Questions** tab when you create or edit a job. Add a question, write the prompt, pick a type, and decide whether it's required. Drag the handle to reorder.

The question types:

| Type | Use it for |
| --- | --- |
| **Short text** | A one-line answer — a city, a notice period, a number. |
| **Long text** | A paragraph — "why this role", a writing sample. |
| **Yes/No** | A clean yes-or-no, like work authorization. |
| **Single select** | Pick one from a list you define. |
| **Multi select** | Pick any number from a list. |
| **URL** | A link — portfolio, GitHub, LinkedIn. |
| **Video upload** | A short recorded answer, up to 250 MB. |

Single select and multi select hold up to 10 options each.

## Required vs optional

Each question has a **Required** toggle. Required questions block submission until they're answered. Optional ones don't.

Be deliberate here. A required question is a small wall between a candidate and the Submit button — some won't climb it. Make a question required only when an empty answer would genuinely stop you from reviewing the application. Everything else can be optional, or left off entirely.

<Callout type="warning" title="Every required question costs you applicants">
Form length has a measurable effect on completion. Each extra required question, especially a long-text or video one, loses you some real candidates — often good ones who are simply busy. Anything you could ask in a five-minute phone screen, ask in the phone screen. The form's job is to get them into your pipeline, not to interview them.
</Callout>

## A note on what isn't here

A couple of things teams sometimes expect:

- **No conditional logic.** Every question on the form shows to every applicant. You can't reveal one question based on the answer to another.
- **No saved question templates.** Questions are configured per job. If two jobs need the same set, you add the questions to each.

If you're standardizing screening questions across many jobs, keep them few and consistent so re-entering them stays quick.

## Limits

The form collects answers; it doesn't score them. A multi-select or Yes/No answer won't auto-filter or auto-reject anyone — a person reads every application. Answers show up on the candidate record alongside the resume. For ranking a stack of applicants by fit, that's what [match rating](/guides/candidates/match-rating) is for, and it reads the resume, not your custom questions.
